'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/shadcn/radio-group';
import { Check } from 'lucide-react';
import Image from 'next/image';

interface MainAngleSelectorProps {
  files: File[];
  isOpen: boolean;
  onClose: () => void;
  onSelect: (mainIndex: number) => void;
}

export function MainAngleSelector({
  files,
  isOpen,
  onClose,
  onSelect,
}: MainAngleSelectorProps) {
  const t = useTranslations('angles.mainSelector');
  const tCommon = useTranslations('common');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [previews, setPreviews] = useState<string[]>([]);

  // Generate previews for all files
  useState(() => {
    const urls: string[] = [];
    files.forEach((file) => {
      const url = URL.createObjectURL(file);
      urls.push(url);
    });
    setPreviews(urls);

    // Cleanup
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  });

  const handleConfirm = () => {
    onSelect(selectedIndex);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-hidden flex flex-col">
          <p className="text-form-help text-muted-foreground mb-4">
            {t('description')}
          </p>

          <div className="flex-1 overflow-y-auto py-4">
            <RadioGroup
              value={selectedIndex.toString()}
              onValueChange={(value) => setSelectedIndex(parseInt(value))}
              className="grid grid-cols-2 md:grid-cols-3 gap-4"
            >
              {files.map((file, index) => (
                <div key={index} className="relative group">
                  <RadioGroupItem
                    value={index.toString()}
                    id={`image-${index}`}
                    className="sr-only"
                  />
                  <label
                    htmlFor={`image-${index}`}
                    className={`
                      cursor-pointer block border-2 rounded-lg overflow-hidden transition-all
                      ${selectedIndex === index ? 'border-primary ring-2 ring-primary ring-offset-2' : 'border-border hover:border-muted-foreground'}
                    `}
                  >
                    <div className="relative aspect-[4/3] bg-muted">
                      {previews[index] && (
                        <Image
                          src={previews[index]}
                          alt={file.name}
                          fill
                          className="object-cover"
                          sizes="(max-width: 768px) 50vw, 33vw"
                        />
                      )}
                      {selectedIndex === index && (
                        <div className="absolute inset-0 bg-primary/10 flex items-center justify-center">
                          <div className="bg-primary rounded-full p-2">
                            <Check className="w-6 h-6 text-primary-foreground" />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-2 bg-background">
                      <p className="text-list-item-value truncate">
                        {file.name}
                      </p>
                      <p className="text-list-item-meta text-muted-foreground">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={onClose}>
              {tCommon('cancel')}
            </Button>
            <Button onClick={handleConfirm}>{t('confirm')}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
